<template>
	<div
		class="min-h-screen bg-slate-50 p-3 sm:p-4 lg:p-6"
	>
		<CreateAppointment v-if="showCreateAppointment" />
		<ViewAppointment />

		<div class="max-w-7xl mx-auto">
			<!-- Header Section -->
			<div
				class="bg-white rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100"
			>
				<div
					class="flex flex-col sm:flex-row justify-between items-center gap-4"
				>
					<div class="flex items-center space-x-2 sm:space-x-3">
						<div
							class="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-lg sm:rounded-xl flex items-center justify-center"
						>
							<svg
								class="w-5 h-5 sm:w-7 sm:h-7 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
						</div>
						<h1 class="font-bold text-2xl sm:text-3xl text-gray-800">
							Appointments
						</h1>
					</div>
					<button
						@click="handleBook"
						class="group relative w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-primary text-white rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center sm:justify-start space-x-2 touch-manipulation"
					>
						<svg
							class="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-90 transition-transform duration-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 4v16m8-8H4"
							/>
						</svg>
						<span class="text-sm sm:text-base">Book Appointment</span>
					</button>
				</div>
			</div>

			<!-- Category Toggle -->
			<div
				class="bg-white rounded-xl sm:rounded-2xl shadow-sm p-3 sm:p-4 mb-4 sm:mb-6 border border-gray-100"
			>
				<div
					class="flex items-center justify-center gap-4 sm:gap-8"
				>
					<!-- Inbox Button -->
					<button
						@click="handleChangeCategory('inbox')"
						class="group relative flex flex-col items-center transition-all duration-300 hover:scale-105"
						:class="selectedCategory === 'inbox' ? 'scale-110' : ''"
					>
						<div
							class="relative flex flex-col items-center justify-center w-20 sm:w-24 lg:w-28 h-20 sm:h-24 lg:h-28 rounded-full transition-all duration-300 transform"
							:class="
								selectedCategory === 'inbox'
									? 'bg-accent shadow-xl'
									: 'bg-gray-100 hover:shadow-lg'
							"
						>
							<!-- Badge for count -->
							<div
								v-if="showIntermediaryAppointments.length > 0"
								class="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg"
							>
								<span class="text-xs font-bold text-gray-800">{{
									showIntermediaryAppointments.length
								}}</span>
							</div>

							<svg
								class="w-8 h-8 sm:w-10 sm:h-12 mb-1 sm:mb-2 transition-colors duration-300"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M1 1V15H15V1H1ZM13 9V3H3V9H6C6 10.1046 6.89543 11 8 11C9.10457 11 10 10.1046 10 9H13Z"
									:fill="
										selectedCategory === 'inbox'
											? '#ffffff'
											: '#4B5563'
									"
								/>
							</svg>
							<span
								class="text-sm font-bold"
								:class="
									selectedCategory === 'inbox'
										? 'text-white'
										: 'text-gray-700'
								"
							>
								Inbox
							</span>
						</div>
						<!-- Active Indicator -->
						<div
							v-if="selectedCategory === 'inbox'"
							class="w-16 h-1 bg-accent rounded-full mt-3"
						></div>
					</button>

					<!-- Sent Button -->
					<button
						@click="handleChangeCategory('sent')"
						class="group relative flex flex-col items-center transition-all duration-300 hover:scale-105"
						:class="selectedCategory === 'sent' ? 'scale-110' : ''"
					>
						<div
							class="relative flex flex-col items-center justify-center w-20 sm:w-24 lg:w-28 h-20 sm:h-24 lg:h-28 rounded-full transition-all duration-300 transform"
							:class="
								selectedCategory === 'sent'
									? 'bg-primary shadow-xl'
									: 'bg-gray-100 hover:shadow-lg'
							"
						>
							<!-- Badge for count -->
							<div
								v-if="showMyAppointments.length > 0"
								class="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-green-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
							>
								<span class="text-xs font-bold text-gray-800">{{
									showMyAppointments.length
								}}</span>
							</div>

							<svg
								class="w-8 h-8 sm:w-10 sm:h-12 mb-1 sm:mb-2 transition-colors duration-300"
								viewBox="0 0 334.5 334.5"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M332.797,13.699c-1.489-1.306-3.608-1.609-5.404-0.776L2.893,163.695c-1.747,0.812-2.872,2.555-2.893,4.481 s1.067,3.693,2.797,4.542l91.833,45.068c1.684,0.827,3.692,0.64,5.196-0.484l89.287-66.734l-70.094,72.1 c-1,1.029-1.51,2.438-1.4,3.868l6.979,90.889c0.155,2.014,1.505,3.736,3.424,4.367c0.513,0.168,1.04,0.25,1.561,0.25 c1.429,0,2.819-0.613,3.786-1.733l48.742-56.482l60.255,28.79c1.308,0.625,2.822,0.651,4.151,0.073 c1.329-0.579,2.341-1.705,2.775-3.087L334.27,18.956C334.864,17.066,334.285,15.005,332.797,13.699z"
									:fill="
										selectedCategory === 'sent'
											? '#ffffff'
											: '#4B5563'
									"
								/>
							</svg>
							<span
								class="text-sm font-bold"
								:class="
									selectedCategory === 'sent'
										? 'text-white'
										: 'text-gray-700'
								"
							>
								Sent
							</span>
						</div>
						<!-- Active Indicator -->
						<div
							v-if="selectedCategory === 'sent'"
							class="w-16 h-1 bg-primary rounded-full mt-3"
						></div>
					</button>
				</div>
			</div>

			<!-- Appointments List - Sent -->
			<section v-if="selectedCategory === 'sent'">
				<div
					v-if="showMyAppointments.length"
					class="bg-white rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 border border-gray-100"
				>
					<div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
						<div
							class="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center"
						>
							<svg
								class="w-4 h-4 sm:w-5 sm:h-5 text-primary"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<h2 class="text-lg sm:text-xl font-bold text-gray-800">
							Sent Appointments
						</h2>
						<span
							class="px-2 sm:px-3 py-1 bg-primary/10 text-primary rounded-full text-xs sm:text-sm font-semibold"
						>
							{{ showMyAppointments.length }} total
						</span>
					</div>

					<div
						class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
					>
						<AppointmentItem
							v-for="(appointment, i) in showMyAppointments"
							:key="i"
							:appointment="appointment"
							@click.native="FetchAppointmentDetails(appointment)"
							class="cursor-pointer transform hover:scale-105 transition-all duration-200"
						/>
					</div>
				</div>

				<!-- Empty State for Sent -->
				<div
					v-else
					class="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-100"
				>
					<div
						class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
					>
						<svg
							class="w-10 h-10 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							/>
						</svg>
					</div>
					<h3 class="text-xl font-bold text-gray-700 mb-2">
						No Sent Appointments
					</h3>
					<p class="text-gray-500 mb-6">
						You haven't sent any appointment requests yet.
					</p>
					<button
						@click="handleBook"
						class="px-6 py-3 bg-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
					>
						Book Your First Appointment
					</button>
				</div>
			</section>

			<!-- Appointments List - Inbox -->
			<section v-if="selectedCategory === 'inbox'">
				<div
					v-if="showIntermediaryAppointments.length"
					class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100"
				>
					<div class="flex items-center space-x-3 mb-6">
						<div
							class="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center"
						>
							<svg
								class="w-5 h-5 text-accent"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
								/>
							</svg>
						</div>
						<h2 class="text-xl font-bold text-gray-800">
							Inbox Appointments
						</h2>
						<span
							class="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm font-semibold"
						>
							{{ showIntermediaryAppointments.length }} total
						</span>
					</div>

					<div
						class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
					>
						<AppointmentItem
							v-for="(
								appointment, i
							) in showIntermediaryAppointments"
							:key="i"
							:appointment="appointment"
							@click.native="FetchAppointmentDetails(appointment)"
							class="cursor-pointer transform hover:scale-105 transition-all duration-200"
						/>
					</div>
				</div>

				<!-- Empty State for Inbox -->
				<div
					v-else
					class="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-100"
				>
					<div
						class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
					>
						<svg
							class="w-10 h-10 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
							/>
						</svg>
					</div>
					<h3 class="text-xl font-bold text-gray-700 mb-2">
						No Appointment Requests
					</h3>
					<p class="text-gray-500">
						You don't have any pending appointment requests.
					</p>
				</div>
			</section>

			<!-- Pagination -->
			<div class="mt-8">
				<Pagination @page-change="fetchAppointments" />
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { FetchIntermediaries } from '../../insurance/api/insurance';
import {
	fecthClientAppointments,
	fetchAppointmentDetails,
	fecthIntermediaryAppointments,
} from '../api/portal';
import { getPageNumbers } from '../../../helpers/pagination';

export default {
	components: {
		CreateAppointment: () => import('./create-appointment.vue'),
		ViewAppointment: () => import('./view-appointment.vue'),
		Modal: () => import('@/components/newportal/templates/others/modal'),
		AppointmentItem: () => import('../templates/others/appointment-item'),
		Pagination: () => import('../templates/others/pagination.vue'),
	},
	data() {
		return {
			selectedCategory: 'sent',
		};
	},
	computed: {
		...mapGetters('appointment', [
			'showCreateAppointment',
			'showMyAppointments',
			'showIntermediaryAppointments',
		]),
	},
	methods: {
		...mapActions('appointment', [
			'setShowCreateAppointments',
			'setMyAppointments',
			'setSelectedAppointment',
			'setIntermediaryAppointments',
		]),
		...mapActions('pagination', [
			'setPagesArray',
			'setTotalPages',
			'setCurrentPage',
		]),
		...mapActions('help', ['setEntities']),

		async handleBook() {
			try {
				const res = await FetchIntermediaries();
				if (res.status) {
					this.setEntities(res.data);
					this.setShowCreateAppointments(!this.showCreateAppointment);
				}
			} catch (error) {
				console.error('Error fetching intermediaries:', error);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: 'Unable to open help center',
					duration: 5000,
				});
			}
		},

		handleChangeCategory(category) {
			this.selectedCategory = category;
		},

		async fetchAppointments() {
			const fetchData = { querryOptions: { page: 1 } };
			try {
				const res = await fecthClientAppointments(fetchData);
				if (res.status) {
					if (Array.isArray(res.data)) {
						this.setMyAppointments(res.data[0].items || []);
						const pages = getPageNumbers(res.data[0].pageCount, 20);
						this.setPagesArray(pages);
						this.setTotalPages(res.data[0].totalCount);
					}
				}
			} catch (err) {
				// Event.$emit('notifications:show', {
				// 	type: 'error',
				// 	title: 'Error',
				// 	message: err.message || 'Failed to fetch appointment',
				// 	duration: 3000,
				// });
			}
		},

		async fetchInterAppointments() {
			const fetchData = { querryOptions: { page: 1 } };
			try {
				const res = await fecthIntermediaryAppointments(fetchData);
				if (res.status) {
					if (Array.isArray(res.data)) {
						this.setIntermediaryAppointments(
							res.data[0].items || [],
						);
						const pages = getPageNumbers(res.data[0].pageCount, 20);
						this.setPagesArray(pages);
						this.setTotalPages(res.data[0].totalCount);
					}
				}
			} catch (err) {
				// Event.$emit('notifications:show', {
				// 	type: 'error',
				// 	title: 'Error',
				// 	message: err.message || 'Failed to process appointments',
				// 	duration: 3000,
				// });
			}
		},

		async FetchAppointmentDetails(appointment) {
			try {
				const response = await FetchIntermediaries();
				if (response.status) {
					this.setEntities(response.data);
				}
				const res = await fetchAppointmentDetails({
					appointmentSystemRef: appointment.appointmentSystemRef,
				});
				if (res.status) {
					this.setSelectedAppointment(res.data);
					this.$store.commit('showAppointmentData', true);
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			}
		},
	},
	mounted() {
		this.fetchAppointments();
		this.fetchInterAppointments();
	},
};
</script>
